import { useEffect, useState } from 'react';
import { leagueFetch } from '@leagueplatform/league-fetch';
import { getCaregiverImpersonationState } from '@leagueplatform/caregiver';
import { captureMessage } from '@leagueplatform/observability';

// Whether someone is a caregiver is a fact about their relationships, not about
// how they signed in, so the backend answers it. The Auth0 connection the login
// used no longer distinguishes a caregiver from a member: once the caregiver
// database is consolidated into the member database both arrive on the same
// connection, and a customer-owned identity arrives on the customer's own.
const CAREGIVER_STATUS_PATH = '/v1/caregiver-status';

// One retry, because a single failed request is more often a blip than a real
// answer and the alternative shown to the user is an error screen.
const REQUEST_ATTEMPTS = 2;

type CaregiverStatusResponse = {
  data?: {
    attributes?: {
      isCaregiver?: boolean;
    };
  };
};

/**
 * `loading` until the first answer arrives, then `caregiver` or `member`.
 * `error` means the status could not be determined.
 */
export type CaregiverStatus = 'loading' | 'caregiver' | 'member' | 'error';

// The answer holds for as long as the caller's relationships do, so it is
// fetched once and shared across every callsite (PrivateRoutesContainer, the
// route guards, useNavLinksList, useUserMenuConfig). `resolvedStatus` lets
// consumers mounting after resolution read it synchronously (no cascading
// `loading` flash / layout shift); `inFlight` dedupes concurrent reads into a
// single request; `subscribers` lets a refresh reach consumers that are already
// mounted and holding a stale answer.
let resolvedStatus: CaregiverStatus | null = null;
let inFlightStatus: Promise<CaregiverStatus> | null = null;
const subscribers = new Set<(status: CaregiverStatus) => void>();

const publish = (status: CaregiverStatus) => {
  subscribers.forEach((notify) => notify(status));
};

const requestCaregiverStatus = async (): Promise<boolean> => {
  // Goes through leagueFetch rather than fetch so the request carries what every
  // other API call carries: the member's bearer token, the client and tenant
  // headers, and — for local development against a test environment — the
  // X-League-Mirrord-Route header. A raw fetch reaches the deployed service
  // instead of the developer's machine, which looks like the endpoint is missing.
  // Error capture is skipped because this hook reports its own failure once, after
  // the retry, rather than once per attempt.
  const response = await leagueFetch(CAREGIVER_STATUS_PATH, {
    headers: { Accept: 'application/vnd.api+json' },
    skipErrorCapture: true,
  });

  if (!response.ok) {
    throw new Error(`Caregiver status request failed with ${response.status}`);
  }

  const body: CaregiverStatusResponse = await response.json();
  const isCaregiver = body.data?.attributes?.isCaregiver;

  // A 200 whose body does not carry the field is not an answer. Treating a
  // missing value as `false` is the failure this whole hook exists to avoid.
  if (typeof isCaregiver !== 'boolean') {
    throw new Error('Caregiver status response carried no verdict');
  }

  return isCaregiver;
};

const readCaregiverStatus = (): Promise<CaregiverStatus> => {
  if (inFlightStatus) return inFlightStatus;

  inFlightStatus = (async () => {
    for (let attempt = 1; attempt <= REQUEST_ATTEMPTS; attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const isCaregiver = await requestCaregiverStatus();
        resolvedStatus = isCaregiver ? 'caregiver' : 'member';
        return resolvedStatus;
      } catch (error) {
        if (attempt === REQUEST_ATTEMPTS) {
          // Deliberately NOT falling back to member. A caregiver shown the
          // member app has no route to their care recipients and no sign that
          // anything went wrong, so an unknown answer must stay unknown.
          captureMessage('Failed to read caregiver status', {
            severityLevel: 'error',
            context: { caregiver: { error: String(error) } },
          });
          // Leave `resolvedStatus` unset and clear the in-flight cache so a
          // later mount can retry.
          inFlightStatus = null;
          return 'error';
        }
      }
    }

    return 'error';
  })();

  return inFlightStatus;
};

/**
 * Discards the cached answer and asks again, telling every mounted consumer.
 *
 * Call this after something that can have changed the caller's relationships.
 * Claiming an invitation is the case that matters: it creates the relationship
 * that makes someone a caregiver, so a "member" answer cached earlier in the
 * session would otherwise survive the claim and keep the new caregiver in the
 * member experience until they happened to reload the page.
 */
export const refreshCaregiverStatus = () => {
  resolvedStatus = null;
  inFlightStatus = null;
  publish('loading');
  readCaregiverStatus().then(publish);
};

/**
 * Asks the backend whether the signed-in user is a caregiver.
 *
 * The answer is fetched once and shared, and re-fetched when
 * `refreshCaregiverStatus` is called.
 *
 * Returns `error` rather than `member` when the answer cannot be determined —
 * callers that gate access on this must show an error, never the member
 * experience.
 */
export const useCaregiverStatus = (): CaregiverStatus => {
  const [status, setStatus] = useState<CaregiverStatus>(
    resolvedStatus ?? 'loading',
  );

  useEffect(() => {
    let cancelled = false;
    const notify = (next: CaregiverStatus) => {
      if (!cancelled) setStatus(next);
    };
    subscribers.add(notify);

    // Already resolved — state was seeded from the cache above, and a later
    // refresh will reach this consumer through its subscription.
    if (resolvedStatus === null) {
      readCaregiverStatus().then(notify);
    }

    return () => {
      cancelled = true;
      subscribers.delete(notify);
    };
  }, []);

  return status;
};

/**
 * Whether the signed-in user is a caregiver.
 *
 * Returns `null` when the answer is not known — either still loading, or the
 * request failed. Callers that decide what a user may reach must distinguish
 * those two with `useCaregiverStatus`; callers that merely decide which
 * navigation links to show can treat both as "no caregiver links yet".
 */
export const useIsCaregiver = (): boolean | null => {
  const status = useCaregiverStatus();

  if (status === 'caregiver') return true;
  if (status === 'member') return false;
  return null;
};

/**
 * True when the signed-in user is a caregiver who has NOT yet started
 * impersonating a care recipient. In that state the app shows the caregiver
 * shell (care-recipient list + limited nav) instead of the member
 * experience. Once impersonating, the caregiver sees the care recipient's
 * normal member app, so this returns `false`.
 *
 * Returns `null` while the caregiver status is unknown.
 */
export const useIsCaregiverShell = (): boolean | null => {
  const isCaregiver = useIsCaregiver();

  if (isCaregiver === null) return null;

  const { impersonateeLeagueUserId } = getCaregiverImpersonationState() ?? {};
  return isCaregiver && !impersonateeLeagueUserId;
};
